import React, { type FC, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { Provider } from 'react-redux';
import { store } from '../../redux/store';
import FinalDecisionMakingSubHeader from './FinalDecisionMakingSubHeader';
import FinalDecisionMakingContainer from './FinalDecisionMakingContainer';
import { useGetListSorts } from '../../hooks/api/ListSort/useGetListSorts';
import { ListSortProps } from '../../utility/types';
import { LoadingSpinner } from '../icons/OurIcons';
import useSessionStorage from '../../hooks/useSessionStorage';

const queryClient = new QueryClient();

type FinalDecisionMakingViewerProps = {
  mapKey: string;
  incomingListSortId: string;
}

export default function FinalDecisionMakingViewer ({ mapKey, incomingListSortId }: FinalDecisionMakingViewerProps) {
  const { storedValue: activeListSortId } = useSessionStorage('activeListSort', incomingListSortId);
  // NOTE: This is the same issue as in FinalDecisionMakingViewer.tsx
  //    this solution is because our navbar is set up in the ruby controller and doesnt have context of front end session
  // before we render the component, we need to check if the incomingListSortId is present in the url
  // if it is, we need to set the activeListSortId to the incomingListSortId
  // if it is not, we need to set the activeListSortId to the activeListSortId in session storage
  // we need to do this because the incomingListSortId is not always present in the url
  // and we need to ensure that the activeListSortId is always present in the url
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!incomingListSortId && activeListSortId) {
      // We have an active list sort id, but no incoming list sort id
      // Refresh the page to update the url with the active list sort id
      const isSlashAtEnd = url.pathname.endsWith('/');
      // If we have an active list sort id, but no incoming list sort id, we need to set the url param to the active list sort id
      window.location.href = url.toString() + `${isSlashAtEnd ? '' : '/'}${activeListSortId}`;
    }
  }, []);

  if (!incomingListSortId && activeListSortId) {
    return (
      <div id='DecisionMakingViewer-loading-spinner-container' className='flex flex-col justify-center items-center h-screen'>
        <LoadingSpinner size='12' />
        <p className='text-md text-gray-500'>Loading Final Decision Making content...</p>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <FinalDecisionMakingViewerChild mapKey={mapKey} incomingListSortId={incomingListSortId} />
      </QueryClientProvider>
    </Provider>
  );
};

const FinalDecisionMakingViewerChild: FC<FinalDecisionMakingViewerProps> = ({ mapKey, incomingListSortId }) => {
  const { data: listSorts, isLoading, error } = useGetListSorts();
  const [ activeListSort, setActiveListSort ] = useState<ListSortProps>(null);
  const { storedValue: activeListSortId, setValue: setActiveListSortId } = useSessionStorage('activeListSort', incomingListSortId);

  useEffect(() => {
    if (listSorts && listSorts.length > 0) {
      if (activeListSortId) {
        const activeListSort = listSorts?.find((listSort) => listSort.id === activeListSortId);
        if (activeListSort) {
          setActiveListSort(activeListSort);
        }
      }
      else {
        setActiveListSort(listSorts[0]);
      }
    }
  }, [ listSorts ]);

  const handleActiveListSortChange = (listSort: ListSortProps) => {
    // NOTE: This is the same code as in DecisionMakingViewer.tsx
    // If you update this, make sure to update DecisionMakingViewer.tsx as well
    if (listSort.id) {
      setActiveListSort(listSort);
      setActiveListSortId(listSort.id);

      const currentURL = window.location.href;
      const url = new URL(currentURL);
      const newUrl = url.origin + '/final-decision-making/' + listSort.id;
      // Update the browser's URL and reload the page so the NAVBAR updates
      window.location.href = newUrl;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    ); // Styled loading state
  }

  if (error) {
    return (
      <div className="text-center text-red-500 mt-4">
        Error fetching list sorts
      </div>
    ); // Styled error state
  }

  return (
    <div className='max-h-full top-0 pb-10 text-xs justify-center'>
      <FinalDecisionMakingSubHeader
        listSorts={listSorts}
        activeListSort={activeListSort}
        setActiveListSort={handleActiveListSortChange}
      />
      <hr className='w-full border-1 border-solid border-gray-200' />
      <FinalDecisionMakingContainer activeListSort={activeListSort} />
    </div>
  );
};
